import { useState } from 'react'
import { formatAllergens } from '@/lib/allergens'
import type { AlternativeGroup, Product, SelectedAlternative } from '@/lib/types'
import { fmt } from '@/lib/utils'
import { useKioskStore } from '@/stores/kiosk-store'

export default function ProductModal() {
  const selectedProduct = useKioskStore((s) => s.selectedProduct)
  const setSelectedProduct = useKioskStore((s) => s.setSelectedProduct)
  const addToCart = useKioskStore((s) => s.addToCart)

  if (!selectedProduct) return null

  return (
    <ProductModalInner
      product={selectedProduct}
      onAdd={(p, alts, msg) => {
        addToCart(p, alts, msg)
        // Stäng produktmodalen direkt så användaren inte hamnar på samma
        // produkt igen efter att addon-flödet (om något) är klart.
        setSelectedProduct(null)
        const menuType = useKioskStore.getState().menuType
        const started = menuType ? useKioskStore.getState().beginAddonFlow(p, menuType) : false
        // Om inget addon-flöde startade: kör post-add-flow.
        // afterProductAddedAction (om satt) går FÖRE backAction — gör att
        // skipCategoryGrid-läget kan stanna kvar i HELA MENYN istället för
        // att hoppa till menuTypeSelect/start.
        if (!started) {
          const store = useKioskStore.getState()
          const postAdd = store.afterProductAddedAction ?? store.backAction
          postAdd?.()
        }
      }}
      onClose={() => setSelectedProduct(null)}
    />
  )
}

function ProductModalInner({ product, onAdd, onClose }: { product: Product; onAdd: (p: Product, alts: SelectedAlternative[], message?: string) => void; onClose: () => void }) {
  const locale = useKioskStore((s) => s.locale)
  // AltGroupMeta: V.2-metadata (layout, allowDuplicates, titleOverride) per
  // native-alternativgrupp. Lagras i pong.altGroupMeta på config-tillen,
  // osynligt för native-appen + gamla kassor.
  const altGroupMeta = useKioskStore((s) => s.altGroupMeta)
  const groups = product.properties?.alternativeGroups || []
  const props = product.properties

  const [itemMessage, setItemMessage] = useState('')
  const [showInfo, setShowInfo] = useState(false)

  // Alternative selections
  const [errorGroups, setErrorGroups] = useState<Set<string>>(new Set())
  const [selections, setSelections] = useState<Record<string, Record<string, number>>>(() => {
    const init: Record<string, Record<string, number>> = {}
    groups.forEach((g) => {
      init[g.id] = {}
      g.alternativeItems.forEach((ai) => {
        if (ai.isDefault) init[g.id][ai.name] = 1
      })
    })
    return init
  })

  const handleSelect = (groupId: string, itemName: string, group: AlternativeGroup) => {
    setErrorGroups((prev) => {
      const next = new Set(prev)
      next.delete(groupId)
      return next
    })
    setSelections((prev) => {
      const curr = { ...(prev[groupId] || {}) }
      const max = group.maxChoices ?? 999
      if (max === 1) {
        const wasSelected = !!curr[itemName]
        return { ...prev, [groupId]: wasSelected ? {} : { [itemName]: 1 } }
      }
      if (curr[itemName]) {
        delete curr[itemName]
      } else {
        const total = Object.values(curr).reduce((s, v) => s + v, 0)
        if (total < max) curr[itemName] = 1
      }
      return { ...prev, [groupId]: curr }
    })
  }

  const handleAdjust = (e: React.MouseEvent | React.TouchEvent, groupId: string, itemName: string, delta: number, group: AlternativeGroup) => {
    e.stopPropagation()
    setSelections((prev) => {
      const curr = { ...(prev[groupId] || {}) }
      const max = group.maxChoices ?? 999
      const newCount = (curr[itemName] || 0) + delta
      if (newCount <= 0) {
        delete curr[itemName]
      } else {
        const otherTotal = Object.entries(curr)
          .filter(([k]) => k !== itemName)
          .reduce((s, [, v]) => s + v, 0)
        if (otherTotal + newCount <= max) curr[itemName] = newCount
      }
      return { ...prev, [groupId]: curr }
    })
  }

  const addonTotal = groups.reduce((total, g) => {
    const sel = selections[g.id] || {}
    return (
      total +
      Object.entries(sel).reduce((sum, [name, count]) => {
        const item = g.alternativeItems.find((ai) => ai.name === name)
        return sum + (item?.addonProduct?.properties?.price || 0) * count
      }, 0)
    )
  }, 0)

  const handleAdd = () => {
    const missing = groups.filter((g) => {
      const min = g.minChoices ?? 0
      if (min === 0) return false
      const total = Object.values(selections[g.id] || {}).reduce((s, v) => s + v, 0)
      return total < min
    })
    if (missing.length > 0) {
      setErrorGroups(new Set(missing.map((g) => g.id)))
      return
    }
    const alts: SelectedAlternative[] = []
    groups.forEach((g) => {
      Object.entries(selections[g.id] || {}).forEach(([name, count]) => {
        if (count <= 0) return
        const item = g.alternativeItems.find((ai) => ai.name === name)
        const alt: SelectedAlternative = {
          alternativeGroupId: g.id,
          alternativeItemName: name,
          count,
        }
        if (item?.addonProduct?.properties) {
          alt.addonProduct = {
            productId: item.addonProduct.properties.id,
            productStateId: item.addonProduct.properties.stateId,
            productPrice: item.addonProduct.properties.price,
          }
        }
        alts.push(alt)
      })
    })
    onAdd(product, alts, itemMessage)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center kiosk-fade-in">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative bg-kiosk-surface w-[92%] max-w-3xl max-h-[90vh] flex flex-col rounded-3xl z-10 kiosk-slide-up overflow-hidden">
        {/* Scrollable content */}
        <div className="overflow-y-auto kiosk-scroll flex-1">
          {/* Image — full width, no crop */}
          {props?.imageUrl && (
            <div className="relative w-full overflow-hidden bg-kiosk-surface-2">
              <img src={props?.imageUrl} alt={product.name} className="w-full h-auto object-contain" />
            </div>
          )}

          <div className="px-6 space-y-5">
            {/* Title + price */}
            <div className="pt-4">
              <div className="flex justify-between items-start gap-3">
                <h3 className="font-bold text-2xl text-kiosk-text">{product.name}</h3>
                {!!props?.price && (
                  <div className="shrink-0 flex flex-col items-end leading-tight">
                    {props.preCampaignPrice && props.preCampaignPrice > props.price && <span className="text-kiosk-muted line-through text-sm">{fmt(props.preCampaignPrice)}</span>}
                    <span className="text-kiosk-accent font-bold text-xl">{fmt(props.price)}</span>
                  </div>
                )}
              </div>
              {props?.description && <p className="text-kiosk-muted text-base mt-2">{props.description}</p>}
            </div>

            {/* Info-knapp ("i" i ring) — oppnar allergener + ingredienser +
                naringsvarden pa begaran. Osynlig om produkten inte har nagot. */}
            {(() => {
              const allergenLabels = formatAllergens(product.allergens, locale)
              const ingredientsText = locale === 'en' ? product.ingredientsTextEn || product.ingredientsTextSv || '' : product.ingredientsTextSv || ''
              // Samla naringsvarden som faktiskt finns (null = ej satt)
              const nutritionRows: { label: string; value: string }[] = []
              const sv = locale === 'sv'
              // mg till g-konvertering for lasbarhet (alla mg-varden)
              const mg = (n: number | null, label: string) => {
                if (n == null) return
                const g = n / 1000
                nutritionRows.push({ label, value: `${g.toFixed(g < 1 ? 2 : 1)} g` })
              }
              if (product.energyKcal != null) {
                nutritionRows.push({
                  label: sv ? 'Energi' : 'Energy',
                  value: `${product.energyKcal} kcal`,
                })
              }
              mg(product.fatMg, sv ? 'Fett' : 'Fat')
              mg(product.saturatedFatMg, sv ? 'varav mättat fett' : 'of which saturated')
              mg(product.carbohydratesMg, sv ? 'Kolhydrater' : 'Carbohydrates')
              mg(product.sugarsMg, sv ? 'varav socker' : 'of which sugars')
              mg(product.proteinMg, sv ? 'Protein' : 'Protein')
              mg(product.saltMg, sv ? 'Salt' : 'Salt')
              mg(product.fiberMg, sv ? 'Fiber' : 'Fibre')

              const hasInfo = allergenLabels.length > 0 || !!ingredientsText.trim() || nutritionRows.length > 0
              if (!hasInfo) return null
              return (
                <div>
                  <button
                    type="button"
                    onClick={() => setShowInfo((v) => !v)}
                    className="kiosk-btn inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-kiosk-surface-2 text-kiosk-muted hover:text-kiosk-text text-sm"
                    aria-expanded={showInfo}
                  >
                    <span className="flex items-center justify-center w-5 h-5 rounded-full border-2 border-current text-[11px] font-bold">i</span>
                    {showInfo ? (sv ? 'Dölj info' : 'Hide info') : sv ? 'Mer info' : 'More info'}
                  </button>
                  {showInfo && (
                    <div className="mt-3 space-y-3 p-4 bg-kiosk-bg border border-kiosk-surface-2 rounded-2xl">
                      {allergenLabels.length > 0 && (
                        <div>
                          <p className="text-amber-400 text-xs font-semibold uppercase tracking-wider mb-1">{sv ? 'Allergener' : 'Allergens'}</p>
                          <p className="text-kiosk-text text-sm">{allergenLabels.join(', ')}</p>
                        </div>
                      )}
                      {ingredientsText && (
                        <div>
                          <p className="text-kiosk-muted text-xs font-semibold uppercase tracking-wider mb-1">{sv ? 'Ingredienser' : 'Ingredients'}</p>
                          <p className="text-kiosk-text text-sm">{ingredientsText}</p>
                        </div>
                      )}
                      {nutritionRows.length > 0 && (
                        <div>
                          <p className="text-kiosk-muted text-xs font-semibold uppercase tracking-wider mb-2">
                            {sv ? 'Näringsvärden' : 'Nutrition'}
                            <span className="text-kiosk-muted/60 font-normal ml-2">({sv ? 'per portion' : 'per serving'})</span>
                          </p>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                            {nutritionRows.map((row) => (
                              <div key={row.label} className="flex justify-between">
                                <span className="text-kiosk-muted">{row.label}</span>
                                <span className="text-kiosk-text">{row.value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })()}

            {/* Native alternativgrupper — enda pathen. V.2-metadata (layout,
                allowDuplicates, titleOverride) appliceras här via altGroupMeta. */}
            {groups.map((group) => {
              const sel = selections[group.id] || {}
              const totalSel = Object.values(sel).reduce((s, v) => s + v, 0)
              const min = group.minChoices ?? 0
              const max = group.maxChoices ?? 999
              const isSingle = max === 1
              const meta = altGroupMeta[group.id] || {}
              const allowDuplicates = meta.allowDuplicates ?? false
              const allowMultiCount = max > 1 && allowDuplicates
              const isRequired = min > 0
              const fulfilled = !isRequired || totalSel >= min
              const hasError = errorGroups.has(group.id)
              const layout = meta.layout || 'list'
              const isGrid = layout === 'grid2' || layout === 'grid3'
              // Engelsk titel kommer från admin-redigerad meta.titleOverrideEn;
              // faller annars tillbaka till svensk override eller native namn.
              const displayTitle = locale === 'en' ? meta.titleOverrideEn || meta.titleOverride || group.name : meta.titleOverride || group.name

              const sv = locale === 'sv'
              let label = sv ? 'Valfritt' : 'Optional'
              if (isRequired) {
                if (isSingle) label = sv ? 'Välj 1' : 'Choose 1'
                else if (min === max) label = `${sv ? 'Välj' : 'Choose'} ${min}`
                else label = `${sv ? 'Välj' : 'Choose'} ${min}${max < 999 ? `-${max}` : '+'}`
              } else if (max < 999) {
                label = `Max ${max}`
              }

              return (
                <div
                  key={group.id}
                  className={`space-y-3 p-4 rounded-2xl transition-all ${
                    hasError ? 'ring-2 ring-red-500/70 bg-red-500/10' : isRequired && !fulfilled ? 'ring-1 ring-kiosk-accent/30 bg-kiosk-accent/5' : ''
                  }`}
                >
                  {/* Centrerad, större fråga (från altGroupMeta.titleOverride eller native-namnet) */}
                  <div className="flex flex-col items-center gap-1.5 pt-1">
                    <h4 className="text-kiosk-text font-bold text-2xl text-center">{displayTitle}</h4>
                    <span
                      className={`text-xs px-3 py-1 rounded-full font-semibold ${
                        hasError
                          ? 'bg-red-500/20 text-red-400'
                          : isRequired && !fulfilled
                            ? 'bg-kiosk-accent text-kiosk-bg'
                            : fulfilled && isRequired
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-kiosk-surface-2 text-kiosk-muted'
                      }`}
                    >
                      {hasError ? (sv ? 'Obligatorisk' : 'Required') : fulfilled && isRequired ? `✓ ${label}` : label}
                    </span>
                  </div>

                  <div className={layout === 'grid2' ? 'grid grid-cols-2 gap-3' : layout === 'grid3' ? 'grid grid-cols-3 gap-3' : 'space-y-2'}>
                    {group.alternativeItems.map((item) => {
                      const isSelected = (sel[item.name] || 0) > 0
                      const count = sel[item.name] || 0
                      const addonPrice = item.addonProduct?.properties?.price || 0
                      const addonImage = item.addonProduct?.properties?.imageUrl || null
                      const itemName = item.name

                      if (isGrid) {
                        // Grid-layout: kort med bild överst, namn + pris under,
                        // check-badge uppe till höger, räknare nere till höger.
                        return (
                          <div
                            key={item.name}
                            onClick={() => handleSelect(group.id, item.name, group)}
                            className={`relative bg-kiosk-bg rounded-2xl overflow-hidden cursor-pointer transition-all ring-2 shadow-lg shadow-black/40 ${
                              isSelected ? 'ring-kiosk-accent' : 'ring-white/10 hover:ring-white/30'
                            }`}
                          >
                            <div className="aspect-4/3 w-full bg-kiosk-surface-2 overflow-hidden">
                              {addonImage ? (
                                <img src={addonImage} alt={itemName} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-kiosk-muted text-4xl">🍽</div>
                              )}
                            </div>
                            {isSelected && (
                              <div className="absolute top-2 right-2 w-9 h-9 rounded-full bg-kiosk-accent flex items-center justify-center shadow-lg">
                                <svg className="w-5 h-5 text-kiosk-bg" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                            <div className="p-3">
                              <div className="text-kiosk-text text-sm font-semibold truncate">{itemName}</div>
                              {addonPrice > 0 && <div className="text-kiosk-accent text-base font-bold mt-0.5">+{fmt(addonPrice)}</div>}
                            </div>
                            {allowMultiCount && isSelected && (
                              <div className="absolute bottom-2 right-2 flex items-center gap-1.5 bg-kiosk-surface/95 rounded-full p-1 backdrop-blur" onClick={(e) => e.stopPropagation()}>
                                <button
                                  onClick={(e) => handleAdjust(e, group.id, item.name, -1, group)}
                                  className="kiosk-btn w-8 h-8 rounded-full bg-kiosk-bg text-kiosk-text flex items-center justify-center text-lg font-bold"
                                >
                                  &minus;
                                </button>
                                <span className="text-kiosk-text text-base w-5 text-center">{count}</span>
                                <button
                                  onClick={(e) => handleAdjust(e, group.id, item.name, 1, group)}
                                  className="kiosk-btn w-8 h-8 rounded-full bg-kiosk-accent text-kiosk-bg flex items-center justify-center text-lg font-bold"
                                >
                                  +
                                </button>
                              </div>
                            )}
                          </div>
                        )
                      }

                      // Default list-layout (rad med checkbox + vänsterbild)
                      return (
                        <div
                          key={item.name}
                          onClick={() => handleSelect(group.id, item.name, group)}
                          className={`flex items-center justify-between p-4 rounded-xl transition-all ${isSelected ? 'bg-kiosk-surface-2 ring-1 ring-kiosk-accent/40' : 'bg-kiosk-bg'}`}
                        >
                          <div className="flex items-center gap-4 flex-1 min-w-0">
                            {/* Checkbox/radio */}
                            <div
                              className={`w-7 h-7 shrink-0 border-2 flex items-center justify-center ${
                                isSingle ? 'rounded-full' : 'rounded-lg'
                              } ${isSelected ? 'border-kiosk-accent bg-kiosk-accent' : 'border-kiosk-muted/40'}`}
                            >
                              {isSelected && (
                                <svg className="w-4 h-4 text-kiosk-bg" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                            {/* Image (if available) */}
                            {addonImage && <img src={addonImage} alt={itemName} className="w-12 h-12 rounded-lg object-cover shrink-0" />}
                            <span className="text-kiosk-text text-base">{itemName}</span>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            {addonPrice > 0 && <span className="text-kiosk-accent text-sm">+{fmt(addonPrice)}</span>}
                            {allowMultiCount && isSelected && (
                              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                <button
                                  onClick={(e) => handleAdjust(e, group.id, item.name, -1, group)}
                                  className="kiosk-btn w-10 h-10 rounded-full bg-kiosk-bg text-kiosk-text flex items-center justify-center text-xl font-bold"
                                >
                                  &minus;
                                </button>
                                <span className="text-kiosk-text text-lg w-6 text-center">{count}</span>
                                <button
                                  onClick={(e) => handleAdjust(e, group.id, item.name, 1, group)}
                                  className="kiosk-btn w-10 h-10 rounded-full bg-kiosk-accent text-kiosk-bg flex items-center justify-center text-xl font-bold"
                                >
                                  +
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
            {/* Special request — "görs om möjligt" / "if possible" */}
            <div>
              <p className="text-kiosk-text font-semibold text-sm mb-1.5">
                {locale === 'en' ? 'Special request' : 'Meddelande'}
                <span className="text-kiosk-muted font-normal ml-2">({locale === 'en' ? 'done if possible' : 'görs om möjligt'})</span>
              </p>
              <textarea
                value={itemMessage}
                onChange={(e) => setItemMessage(e.target.value.slice(0, 200))}
                placeholder={locale === 'en' ? 'E.g. no peanuts, mild spice, etc.' : 'T.ex. utan jordnötter, milt, etc.'}
                rows={2}
                className="w-full bg-kiosk-bg border border-kiosk-surface-2 rounded-xl px-3 py-2 text-kiosk-text placeholder:text-kiosk-muted text-sm focus:outline-none focus:border-kiosk-accent/60 resize-none"
              />
            </div>

            {/* Add + Cancel buttons — after all alternative groups */}
            <div className="space-y-2 pt-2 pb-4">
              <button onClick={handleAdd} className="kiosk-btn w-full py-4 bg-kiosk-accent text-kiosk-bg font-bold rounded-xl text-lg">
                {locale === 'en' ? 'Add' : 'Lägg till'} — {fmt((props?.price ?? 0) + addonTotal)}
              </button>
              <button onClick={onClose} className="kiosk-btn w-full py-2 text-kiosk-muted text-sm">
                {locale === 'en' ? 'Cancel' : 'Avbryt'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
